import React from 'react';
import { AlertTriangle, Megaphone, Workflow } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import AutomationStepList from './AutomationStepList';
import { humanize, triggerSummary } from './automationFormat';

const Row = ({ label, children }) => (
  <div className="flex gap-2">
    <span className="w-20 shrink-0 text-muted-foreground">{label}</span>
    <span className="min-w-0 break-words text-foreground">{children}</span>
  </div>
);

const Section = ({ icon: Icon, title, badge, children }) => (
  <section className="space-y-1.5 rounded-md border border-border p-2">
    <div className="flex items-center gap-1.5 font-medium text-foreground">
      <Icon className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
      <span>{title}</span>
      {badge && <Badge variant="outline" className="h-[18px] px-1.5 text-2xs">{badge}</Badge>}
    </div>
    {children}
  </section>
);

/** Approval details for activating a campaign through a new workflow. */
export default function LaunchCampaignParams({ params = {}, action = {} }) {
  const manual = params.trigger_type === 'manual';
  const campaign = params.new_campaign;
  const reviewedCampaign = campaign || action.campaign_snapshot;
  const campaignName = campaign?.name || action.target_name || params.campaign_template_id;
  const accountNames = action.account_names?.length ? action.account_names : params.account_ids;
  const workflowSteps = [
    ...(params.steps_before_activation || []),
    {
      action_type: 'activate_campaign',
      label: 'Activate Campaign',
      config: { campaign_name: campaignName },
    },
  ];
  const conditions = triggerSummary(params.trigger_type, params.trigger_config);

  return (
    <div className="mt-2 space-y-2 text-xs">
      <div
        role="note"
        className="flex items-start gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 text-amber-800 dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
      >
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        <span>
          {manual
            ? 'Approval creates and activates this workflow, then runs it now for the selected accounts.'
            : 'Approval activates this workflow. Every account in its audience that matches the trigger is enrolled, including accounts that match now.'}
        </span>
      </div>

      <Section icon={Megaphone} title="Campaign" badge={campaign ? 'New' : 'Existing'}>
        <Row label="Name">{campaignName}</Row>
        {reviewedCampaign?.goal && <Row label="Goal">{reviewedCampaign.goal}</Row>}
        {reviewedCampaign?.description && <Row label="Description">{reviewedCampaign.description}</Row>}
        <AutomationStepList title="Campaign Steps" items={reviewedCampaign?.steps} />
        {!reviewedCampaign && (
          <p role="note" className="text-amber-700 dark:text-amber-400">
            This older proposal has no campaign content snapshot. Request a new proposal to review its steps.
          </p>
        )}
      </Section>

      <Section icon={Workflow} title="Workflow" badge="New">
        <Row label="Name">{params.workflow_name}</Row>
        <Row label="Trigger">{humanize(params.trigger_type)}</Row>
        {conditions && <Row label="Conditions">{conditions}</Row>}
        {manual && accountNames?.length > 0 && (
          <Row label={accountNames.length === 1 ? 'Account' : `Accounts (${accountNames.length})`}>
            {accountNames.join(', ')}
          </Row>
        )}
        {params.workflow_description && (
          <p className="line-clamp-3 whitespace-pre-wrap text-muted-foreground">
            {params.workflow_description}
          </p>
        )}
        <AutomationStepList title="Workflow Steps" items={workflowSteps} />
      </Section>
    </div>
  );
}
